/**
 * Structured guidance shown on demand for each exposure question
 * in the Telecom Pre-Investment Cyber Risk Triage questionnaire.
 *
 * Rendered inside an expandable panel triggered by a "Guidance" link
 * next to each question. Plain business language, concise, no jargon.
 */

export type TriageGuidance = {
  meaning: string;          // What this means
  lookFor: string[];        // What to look for
  low: string;              // How to answer — Low
  medium: string;           // How to answer — Medium
  high: string;             // How to answer — High
};

export const TRIAGE_QUESTION_GUIDANCE: Record<string, TriageGuidance> = {
  // ---------- RED FLAGS ----------
  TPT_RF_01: {
    meaning:
      "Whether a known, material cyber incident has affected the company — directly or through a critical dependency.",
    lookFor: [
      "Reported breaches, ransomware events, or data exposure",
      "Regulator-notified incidents or public disclosures",
      "Unresolved investigations or active remediation",
    ],
    low: "No known major cyber incident.",
    medium: "Past major incident, now resolved.",
    high: "Active or unresolved major cyber incident.",
  },
  TPT_RF_02: {
    meaning:
      "Whether the company is exposed to sanctions, enforcement actions, or hostile-state ownership/influence.",
    lookFor: [
      "Sanctions lists or active enforcement actions",
      "Material hostile-state ownership stakes or board influence",
      "Cyber-related regulatory investigations",
    ],
    low: "No sanctions or hostile-state exposure.",
    medium: "Limited or historical exposure under remediation.",
    high: "Active sanctions, enforcement, or material hostile-state influence.",
  },
  TPT_RF_03: {
    meaning:
      "Whether a regulator-flagged high-risk vendor operates in the company's core network or signaling layer.",
    lookFor: [
      "Use of vendors flagged by major regulators (banned or restricted equipment)",
      "Presence in core network, signaling, or lawful-intercept paths",
      "Replacement or containment plans in flight",
    ],
    low: "No flagged high-risk vendor in core or signaling.",
    medium: "Limited exposure with active replacement plan.",
    high: "Material reliance on a flagged high-risk vendor in core or signaling.",
  },

  // ---------- M1 ----------
  TPT_M1_03: {
    meaning:
      "How transparent and stable the ultimate ownership of the company is.",
    lookFor: [
      "Identifiable ultimate beneficial owners",
      "Frequency of ownership or sponsor changes",
      "Use of opaque holding structures or offshore vehicles",
    ],
    low: "Long-term, transparent ownership with stable sponsors.",
    medium: "Some ownership change in recent years, or partly opaque structure.",
    high: "Frequently changing or opaque ownership; unclear ultimate control.",
  },
  TPT_M1_04: {
    meaning:
      "How concentrated revenue is across a small number of customers or contracts.",
    lookFor: [
      "Top 1, 3, and 5 customers as % of revenue",
      "Concentration in a single contract or government tender",
      "Length and renewal risk of major contracts",
    ],
    low: "No customer represents more than ~20% of revenue.",
    medium: "Top customer ~20–50% of revenue, or top 3 dominate.",
    high: "Single customer >50–70% of revenue, or critical contract at risk.",
  },
  TPT_M1_05: {
    meaning:
      "Whether a near-term sale, IPO, or major restructuring is expected.",
    lookFor: [
      "Public exit timelines or sponsor fund maturity",
      "Active sale process or IPO preparation",
      "Restructuring or carve-out activity",
    ],
    low: "No exit event expected in the next 24 months.",
    medium: "Exit possible within 12–24 months but not committed.",
    high: "Active sale, IPO, or restructuring underway or imminent.",
  },

  // ---------- M3 ----------
  TPT_M3_04: {
    meaning:
      "Whether the operator is in the middle of a significant technology shift.",
    lookFor: [
      "Active migrations (e.g., 4G→5G, on-prem→cloud, legacy→virtualised core)",
      "Parallel-run environments and dual operations",
      "Pace and scope of transformation programmes",
    ],
    low: "Stable estate; no major transition in progress.",
    medium: "One significant transition underway with controlled scope.",
    high: "Multiple concurrent transitions or aggressive migration timeline.",
  },

  // ---------- M5 ----------
  TPT_M5_03: {
    meaning:
      "How dependent the operator is on third parties for running critical operations.",
    lookFor: [
      "Outsourced NOC/SOC, managed core, or managed RAN",
      "Single managed-service provider across multiple critical functions",
      "Limited in-house ability to operate without the provider",
    ],
    low: "Critical operations primarily run in-house.",
    medium: "Selective outsourcing of non-core operational functions.",
    high: "Core operations heavily outsourced, often to a single provider.",
  },

  // ---------- M6 ----------
  TPT_M6_03: {
    meaning:
      "Track record of material cyber or operational incidents.",
    lookFor: [
      "Reported breaches, outages, or regulator-notified incidents",
      "Repeat incidents or unresolved root causes",
      "Public disclosures, fines, or customer-impacting events",
    ],
    low: "No material incidents in the last 24 months.",
    medium: "One notable incident, contained and remediated.",
    high: "Multiple or unresolved incidents, or a recent major event.",
  },
  TPT_M6_04: {
    meaning:
      "Signs of financial pressure that typically slow security investment.",
    lookFor: [
      "Negative EBITDA, covenant pressure, or refinancing risk",
      "Recent cost-cutting, layoffs, or hiring freezes",
      "Deferred capex on technology or security",
    ],
    low: "Healthy financials; security investment on track.",
    medium: "Some financial pressure or cost-control measures.",
    high: "Material financial stress; deferred or frozen security spend.",
  },

  // ---------- M7 ----------
  TPT_M7_01: {
    meaning:
      "How broad the population with privileged or administrative access is.",
    lookFor: [
      "Number and scope of privileged accounts",
      "Use of shared admin credentials",
      "Privileged Access Management (PAM) coverage",
    ],
    low: "Small, well-scoped privileged user population.",
    medium: "Standard privileged population including some external operators.",
    high: "Very broad privileged access spanning vendors, partners, and offshore teams.",
  },
  TPT_M7_02: {
    meaning:
      "How much administrative access is held by third parties.",
    lookFor: [
      "Vendor/MSP admin access into core, billing, or signaling systems",
      "Persistent vs. just-in-time access",
      "Cross-border or offshore administrative access",
    ],
    low: "Minimal or tightly bounded third-party admin access.",
    medium: "Defined third-party admin access with typical vendor footprint.",
    high: "Extensive, persistent third-party admin access, including cross-border operators.",
  },
};

export function getTriageGuidance(
  questionKey?: string | null
): TriageGuidance | null {
  if (!questionKey) return null;
  return TRIAGE_QUESTION_GUIDANCE[questionKey] ?? null;
}
